/**
 * GiftStreakModal — Admin-only utility for gifting a user a streak.
 * Extracted from AdminDashboard.jsx (Jun 4 2026) to thin out the parent.
 */
import React from 'react';
import { Flame, X } from 'lucide-react';

const GiftStreakModal = ({
  giftStreakTarget,
  giftStreakDays,
  giftStreakReason,
  giftStreakBusy,
  setGiftStreakDays,
  setGiftStreakReason,
  onClose,
  onSubmit,
}) => {
  if (!giftStreakTarget) return null;
  return (
    <div
      className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
      data-testid="gift-streak-modal"
    >
      <div
        className="bg-slate-900 border border-slate-800 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-md p-5 sm:p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Flame className="w-5 h-5 text-amber-400" />
            <h2 className="text-base font-bold text-white">Gift a streak</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-md hover:bg-slate-800 text-slate-400"
            data-testid="gift-streak-close"
          >
            <X className="w-4 h-4" />
          </button>
        </header>
        <p className="text-xs text-slate-400 mb-4">
          Gifting <span className="text-amber-200 font-semibold">{giftStreakTarget.name}</span>.
          We won't downgrade an existing higher streak.
        </p>
        <label className="block mb-3">
          <span className="text-[10px] uppercase tracking-widest font-bold text-slate-400">Days (1–365)</span>
          <div className="flex items-center gap-2 mt-1">
            {[7, 14, 30, 100].map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setGiftStreakDays(d)}
                className={`px-3 py-1.5 rounded-md text-xs font-bold border transition ${
                  Number(giftStreakDays) === d
                    ? 'bg-amber-500 text-slate-900 border-amber-400'
                    : 'bg-slate-800 text-slate-300 border-slate-700 hover:border-amber-400/60'
                }`}
                data-testid={`gift-streak-quick-${d}`}
              >
                {d}d
              </button>
            ))}
            <input
              type="number"
              min="1"
              max="365"
              value={giftStreakDays}
              onChange={(e) => setGiftStreakDays(e.target.value)}
              className="flex-1 w-20 rounded-md bg-slate-950 border border-slate-700 text-white text-sm px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-amber-500/40"
              data-testid="gift-streak-days-input"
            />
          </div>
        </label>
        <label className="block mb-5">
          <span className="text-[10px] uppercase tracking-widest font-bold text-slate-400">Reason (audit log)</span>
          <textarea
            value={giftStreakReason}
            onChange={(e) => setGiftStreakReason(e.target.value.slice(0, 500))}
            rows={3}
            placeholder="e.g. goodwill after outage on Jun 1, partner code redemption…"
            className="mt-1 w-full rounded-md bg-slate-950 border border-slate-700 text-white text-sm px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-amber-500/40"
            data-testid="gift-streak-reason-input"
          />
          <span className="block text-[10px] text-slate-500 text-right mt-0.5">{giftStreakReason.length} / 500</span>
        </label>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={giftStreakBusy}
            className="flex-1 px-4 py-2 rounded-lg border border-slate-700 text-slate-200 hover:bg-slate-800 text-sm font-semibold"
            data-testid="gift-streak-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={giftStreakBusy}
            className="flex-1 px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 disabled:bg-slate-700 text-slate-900 text-sm font-bold inline-flex items-center justify-center gap-1.5"
            data-testid="gift-streak-submit"
          >
            <Flame className="w-4 h-4" /> Gift {giftStreakDays || 0} day{Number(giftStreakDays) === 1 ? '' : 's'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GiftStreakModal;
